// swimlaneArrows — the SVG overlay edge renderers for the swimlane board:
// gateway diamonds + legs, the step-travel droplet(s), and the flow/sequence
// step arrows. Pure presentational components: every one takes its GEOMETRY as
// props — from scene's `swimlane-edges` (gateway legs / flow edges), the single
// source shared with the export composer — and emits SVG. The live paint (hex
// stroke + SMIL marker) and interactive identity (`data-edge-id` / branch pick)
// are resolved HERE, at the render site, from each leg's role/branch — never in
// an intermediate adapter. No layout math beyond trivial local marker shapes.

import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { Row, NodeId } from "@archlang/engine";
import {
  LANE_WIDTH,
  BLOCK_INSET,
  type SwimlaneGwGeom,
  type SwimlaneGwLeg,
} from "@archlang/scene/process";
import { svgFragmentUrl } from "../svgFragmentUrl";
import {
  bfsEdgePath,
  dropletChain,
  legDuration,
  roundedPolyPath,
  type FlowGraph,
  type GatewayLegIds,
} from "./flow-graph";
import { blockYBounds, type RowGeometry } from "./swimlaneRows.js";
import type { StepEdgeStyle } from "./swimlaneTypes.js";

/** A gateway leg's resolved render paint — the live-view hex stroke + SMIL
 *  marker + interactive identity for one scene `SwimlaneGwLeg`. Live paint is
 *  hardcoded hex (NOT scene tokens — the export backend owns those); this is
 *  the live half of the two-backend split. */
interface LegPaint {
  d: string;
  color: string;
  marker: string;
  label?: string;
  labelColor?: string;
  lx?: number;
  ly?: number;
  anchor?: "start" | "middle" | "end";
  pick?: string;
  edgeId?: string;
}

/** Map a scene gateway leg → its live paint + identity. Colours mirror the
 *  export tokens; edge ids come from the flow graph, keyed by branch label. */
function paintLeg(leg: SwimlaneGwLeg, selectable: boolean, edgeIds: GatewayLegIds | undefined): LegPaint {
  if (leg.role === "fork") return { d: leg.d, color: "#475569", marker: "sw-flow-tick", edgeId: edgeIds?.fork };
  if (leg.role === "cont") return { d: leg.d, color: "#475569", marker: "sw-flow-tick", edgeId: edgeIds?.cont };
  if (leg.role === "head") {
    return {
      d: leg.d,
      color: leg.isCatch ? "#ef4444" : "#64748b",
      marker: leg.isCatch ? "sw-flow-tick-err" : "sw-flow-tick",
      label: leg.label,
      labelColor: leg.isCatch ? "#f87171" : "#94a3b8",
      lx: leg.labelX, ly: leg.labelY, anchor: "start",
      pick: selectable ? leg.branchLabel : undefined,
      edgeId: leg.branchLabel !== undefined ? edgeIds?.heads.get(leg.branchLabel) : undefined,
    };
  }
  // tail
  return {
    d: leg.d,
    color: leg.isCatch ? "#ef4444" : "#475569",
    marker: leg.isCatch ? "sw-flow-tick-err" : "sw-flow-tick",
    edgeId: leg.branchLabel !== undefined ? edgeIds?.tails.get(leg.branchLabel) : undefined,
  };
}

export function GatewayArrows({
  gw,
  edgeIds,
  selectable,
  selected = false,
  onBranchSelect,
  onBlockDragDown,
  dragging = false,
}: {
  /** Scene gateway geometry (diamonds + legs) — geometry only. */
  gw: SwimlaneGwGeom;
  /** Flow-graph leg edge ids for this frame (fork / per-branch heads+tails / cont). */
  edgeIds: GatewayLegIds | undefined;
  /** Only exclusive frames (if/try) are pickable; select/parallel run all arms. */
  selectable: boolean;
  selected?: boolean;
  onBranchSelect?: (nodeId: NodeId, label: string) => void;
  /** Whole-block drag grip (P2 #19, edit mode): pressing the SPLIT diamond arms
   *  a drag that relocates the ENTIRE block (moveStep on the frame nodeId). */
  onBlockDragDown?: (e: ReactPointerEvent) => void;
  /** True while THIS block is being dragged — dim the diamonds. */
  dragging?: boolean;
}) {
  const HALF = 9;
  const { frameId: fid, splitX, splitCy, mergeX, mergeCy, hiddenCount, mergeAspect, mergeKind, detached } = gw;
  const collapsed = hiddenCount > 0;
  const paths = gw.legs.map((leg) => paintLeg(leg, selectable, edgeIds));
  const triangle = (dx: number, dy: number, kind: string, up: boolean) => {
    const HW = 13; // wider than tall
    const pts: [number, number][] = up
      ? [[dx, dy - HALF], [dx + HW, dy + HALF], [dx - HW, dy + HALF]]
      : [[dx, dy + HALF], [dx - HW, dy - HALF], [dx + HW, dy - HALF]];
    return (
      <path
        data-gateway={kind}
        d={roundedPolyPath(pts, 4)}
        fill={collapsed ? "#3a2e08" : "#0f172a"}
        stroke={collapsed ? "#fbbf24" : "#94a3b8"}
        strokeWidth={collapsed ? 2 : 1.5}
        strokeLinejoin="round"
      />
    );
  };
  const pickBranch = (label: string) => onBranchSelect?.(fid as NodeId, label);
  const clearPick = () => onBranchSelect?.(fid as NodeId, "");
  return (
    <g data-gateway-frame={fid} style={dragging ? { opacity: 0.4 } : undefined}>
      {paths.map((p, i) => {
        const clickable = !!p.pick && !!onBranchSelect;
        return (
          <g
            key={i}
            data-branch-pick={p.pick}
            onClick={clickable ? () => pickBranch(p.pick!) : undefined}
            style={clickable ? { cursor: "pointer", pointerEvents: "auto" } : undefined}
          >
            {/* Fat transparent hit target so the thin arrow body is easy to click. */}
            {clickable && <path d={p.d} fill="none" stroke="transparent" strokeWidth={12} />}
            <path data-edge-id={p.edgeId} d={p.d} fill="none" stroke={p.color} strokeWidth={1} markerEnd={svgFragmentUrl(p.marker)} />
            {p.label && (
              <text x={p.lx} y={p.ly} fontSize={9} fill={p.labelColor ?? p.color} textAnchor={p.anchor ?? "middle"}>{p.label}</text>
            )}
          </g>
        );
      })}
      {/* Clicking a triangle clears the pick → all branches show again. */}
      <g
        onClick={selectable && selected && onBranchSelect ? clearPick : undefined}
        style={selectable && selected && onBranchSelect ? { cursor: "pointer", pointerEvents: "auto" } : undefined}
      >
        {collapsed && (
          <title>{`Showing 1 of ${hiddenCount + 1} paths — click to show all`}</title>
        )}
        {/* Split diamond doubles as the whole-block DRAG grip in edit mode (a
            transparent fat hit target under it arms the drag; a click still
            clears the branch pick via the enclosing <g>). */}
        {onBlockDragDown ? (
          <g
            data-block-drag={fid}
            style={{ cursor: "grab", pointerEvents: "auto" }}
            onPointerDown={onBlockDragDown}
          >
            <title>Drag to move this whole block</title>
            <circle cx={splitX} cy={splitCy} r={16} fill="transparent" />
            {triangle(splitX, splitCy, "split", true)}
          </g>
        ) : (
          triangle(splitX, splitCy, "split", true)
        )}
        {/* Detached `parallel out`: no merge diamond — the branches never rejoin. */}
        {!detached && triangle(mergeX, mergeCy, "merge", false)}
        {/* Merge-type glyph: `+` AND-join (keep losers) / `×` race (cancel
            losers) / clock await-boundary — distinct from just a side label. */}
        {!detached && mergeKind && (
          <g data-merge-kind={mergeKind}>
            {mergeKind === "join" && (
              <path d={`M ${mergeX - 3} ${mergeCy - 1} L ${mergeX + 3} ${mergeCy - 1} M ${mergeX} ${mergeCy - 4} L ${mergeX} ${mergeCy + 2}`} stroke="#94a3b8" strokeWidth={1.25} fill="none" />
            )}
            {mergeKind === "race" && (
              <path d={`M ${mergeX - 2.6} ${mergeCy - 3.6} L ${mergeX + 2.6} ${mergeCy + 1.6} M ${mergeX + 2.6} ${mergeCy - 3.6} L ${mergeX - 2.6} ${mergeCy + 1.6}`} stroke="#94a3b8" strokeWidth={1.25} fill="none" />
            )}
            {mergeKind === "await" && (
              <>
                <circle cx={mergeX} cy={mergeCy - 1} r={3.2} stroke="#94a3b8" strokeWidth={1} fill="none" />
                <path d={`M ${mergeX} ${mergeCy - 2.8} V ${mergeCy - 1} L ${mergeX + 1.5} ${mergeCy - 0.1}`} stroke="#94a3b8" strokeWidth={1} fill="none" />
              </>
            )}
          </g>
        )}
        {mergeAspect && (
          <text
            data-merge-threshold={mergeAspect}
            x={(detached ? splitX : mergeX) + 16}
            y={(detached ? splitCy : mergeCy) + 3}
            fontSize={9} fontWeight={600} fill="#94a3b8"
          >{mergeAspect}</text>
        )}
        {collapsed && (
          <text x={splitX + 16} y={splitCy + 3} fontSize={9} fontWeight={600} fill="#fbbf24">+{hiddenCount}</text>
        )}
      </g>
    </g>
  );
}

/**
 * A glowing droplet that travels a poly-path once at constant speed and fades
 * on arrival. Auto-play is slower (240 px/s) than a manual next (680 px/s).
 * `animKey` should change per traversal so React remounts → the SMIL replays.
 */
export function TravelDroplet({ d, autoPlay, animKey, delaySec = 0 }: { d: string; autoPlay: boolean; animKey: string | number; delaySec?: number }) {
  const speed = autoPlay ? 240 : 680; // px/s
  const dur = legDuration(d, speed);
  const motionRef = useRef<SVGAnimateMotionElement>(null);
  const fadeRef = useRef<SVGAnimateElement>(null);
  // SMIL with no `begin` starts at document time 0 — so it would fire once at
  // page load and never on a later insertion (a scrub). `begin="indefinite"`
  // + beginElement() on (re)mount makes it play exactly when inserted/keyed.
  // `delaySec` chains sequential legs (fork→split THEN split→head).
  useEffect(() => {
    const fire = () => {
      try {
        motionRef.current?.beginElement?.();
        fadeRef.current?.beginElement?.();
      } catch { /* jsdom has no SMIL */ }
    };
    if (delaySec > 0) {
      const t = setTimeout(fire, delaySec * 1000);
      return () => clearTimeout(t);
    }
    fire();
    return undefined;
  }, [animKey, delaySec]);
  return (
    <g style={{ pointerEvents: "none" }}>
      <circle r={4} fill="#7dd3fc" filter={svgFragmentUrl("sw-droplet-glow")} opacity={0}>
        <animateMotion ref={motionRef} dur={`${dur}s`} path={d} fill="freeze" begin="indefinite" repeatCount={1} />
        <animate ref={fadeRef} attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.75;1" dur={`${dur}s`} fill="freeze" begin="indefinite" repeatCount={1} />
      </circle>
    </g>
  );
}

/**
 * The single step-travel droplet, driven by the flow GRAPH. On each step
 * transition it BFS-walks from the previous active step to the current one,
 * reads each edge's measured `d` from the rendered overlay, and animates the
 * chain in sequence (delays chain the legs). This flows correctly through
 * branch markers — A→split→B and tail→merge→split→head — without per-arrow
 * heuristics.
 */
export function StepDroplets({
  graph,
  activeStepIndex,
  animSeq,
  autoPlay,
  overlayRef,
  fromRef,
  onArrive,
}: {
  graph: FlowGraph;
  activeStepIndex: number | null;
  animSeq: number;
  autoPlay: boolean;
  overlayRef: { current: SVGSVGElement | null };
  /** Origin step for this transition (the immediately-prior active step),
   *  captured by the parent on every change so a manual jump can't leave it
   *  stale. */
  fromRef: { current: number | null };
  /** Called when the droplet REACHES the target — the destination highlights
   *  only then (not when the transition is requested). */
  onArrive: (stepIndex: number | null) => void;
}) {
  const [chain, setChain] = useState<{ id: string; d: string; delaySec: number }[]>([]);
  useLayoutEffect(() => {
    if (animSeq === 0) return;
    const from = fromRef.current;
    if (activeStepIndex == null) { setChain([]); onArrive(null); return; }
    const fromKey = from != null ? graph.stepToKey.get(from) ?? null : null;
    const toKey = graph.stepToKey.get(activeStepIndex) ?? null;
    const ids = bfsEdgePath(graph.adj, fromKey, toKey);
    const speed = autoPlay ? 240 : 680; // px/s
    const readD = (id: string) => overlayRef.current?.querySelector(`[data-edge-id="${id}"]`)?.getAttribute("d") ?? null;
    const { legs, totalDur } = dropletChain(graph, ids, readD, speed);
    setChain(legs);
    // Highlight the destination only when the ball arrives (end of the chain).
    const t = setTimeout(() => onArrive(activeStepIndex), Math.max(0, totalDur * 1000));
    return () => clearTimeout(t);
  }, [animSeq]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <g>
      {chain.map((c) => (
        <TravelDroplet key={`${c.id}-${animSeq}`} d={c.d} autoPlay={autoPlay} delaySec={c.delaySec} animKey={`${c.id}-${animSeq}`} />
      ))}
    </g>
  );
}

export function FlowArrow({
  row,
  prevRow,
  isEntry,
  active,
  edgeStyle,
  geom,
  laneIndex,
  rowTopPad,
  rowBottomPad,
  laneOccupied,
  edgeId,
  d,
}: {
  row: Row;
  prevRow: Row | null;
  isEntry: boolean;
  active: boolean;
  edgeStyle: StepEdgeStyle | undefined;
  geom: RowGeometry;
  laneIndex: (id: string) => number;
  rowTopPad: (y: number) => number;
  rowBottomPad: (y: number) => number;
  /** Flow-graph edge id (prev→this), tagged on the arrow so the central
   *  droplet can traverse it. */
  edgeId?: string;
  /** The arrow's path, computed once on the flow graph (`flowEdgeGeom`). */
  d?: string;
  /** True when a lane already holds a block on the given row. */
  laneOccupied: (y: number, lane: number) => boolean;
}) {
  const fromI = laneIndex(row.fromLaneId);
  const toI = laneIndex(row.toLaneId);
  if (toI < 0) return null;
  const blockX = toI * LANE_WIDTH + BLOCK_INSET;
  const blockW = LANE_WIDTH - BLOCK_INSET * 2;
  const { blockCy: cy } = blockYBounds(row.y, toI, geom, rowTopPad(row.y), rowBottomPad(row.y));
  const widgetColor = !active && edgeStyle?.color ? edgeStyle.color : undefined;
  const dotStroke = active ? "#38bdf8" : widgetColor ?? "#94a3b8";
  // A saga compensation (unwind) step is the reverse / undo direction — draw it
  // dashed in amber so the rollback path stands apart from forward flow.
  const compensation = edgeStyle?.compensation === true;
  // A `dist` step is choreographed (no coordinator) — mark it so the board
  // distinguishes choreography from orchestration (PROCESS.md §2).
  const dist = edgeStyle?.dist === true;

  // Geometry comes from the flow graph (computed in `flowEdgeGeom`), so the
  // arrow renders FROM the graph rather than recomputing its own path.
  const ctrlD = d ?? null;

  // The entry-dot stub for the FIRST real step (it has no predecessor edge).
  // Lifted out so the comment label can anchor to it when `ctrlD` is absent.
  const entryStub = isEntry && fromI >= 0 ? (() => {
    const callerCx = fromI * LANE_WIDTH + LANE_WIDTH / 2;
    const goingRight = toI >= fromI;
    // Keep the dot at the caller-lane centre normally, but if that lane already
    // holds a block on this row (a serpentine merge can land one there) — or it's
    // a self-call — tuck the dot just outside the first block so it always
    // reserves its own clear space.
    const overlaps = fromI === toI || laneOccupied(row.y, fromI);
    const dotX = overlaps ? (goingRight ? blockX - 14 : blockX + blockW + 14) : callerCx;
    const targetX = dotX < blockX ? blockX : blockX + blockW;
    return { dotX, targetX };
  })() : null;

  return (
    <g
      data-flow-step={row.stepIndex ?? undefined}
      data-flow-prev={prevRow?.stepIndex ?? ""}
      data-dist={dist ? "true" : undefined}
    >
      {dist && (
        <text x={blockX} y={cy - 12} fontSize={8} fontWeight={600} fill="#a78bfa">dist</text>
      )}
      {ctrlD && (
        <path
          data-edge-id={edgeId}
          data-compensation={compensation ? "true" : undefined}
          d={ctrlD}
          fill="none"
          stroke={compensation ? "#f59e0b" : "#475569"}
          strokeWidth={active ? 1.5 : 1}
          strokeDasharray={compensation ? "4 3" : undefined}
          markerEnd={svgFragmentUrl("sw-flow-tick")}
        />
      )}
      {/* The trailing call comment is drawn by the dedicated over-all-rows pass
          in the parent (so it survives gatewaySkip / comp rows this arrow skips);
          FlowArrow only draws the edge + entry stub. */}
      {entryStub && (
        <g>
          <circle cx={entryStub.dotX} cy={cy} r={5} fill={dotStroke} />
          <path
            d={`M ${entryStub.dotX} ${cy} L ${entryStub.targetX} ${cy}`}
            stroke={dotStroke}
            strokeWidth={active ? 1.5 : 1.25}
            fill="none"
            markerEnd={svgFragmentUrl("sw-flow-tick")}
          />
        </g>
      )}
    </g>
  );
}

export function SequenceArrow({
  row,
  isEntry,
  active,
  dimmed = false,
  edgeStyle,
  geom,
  laneIndex,
  rowTopPad,
  rowBottomPad,
}: {
  row: Row;
  isEntry: boolean;
  active: boolean;
  /** True for a step inside an unselected branch alternative — the arrow fades
   *  with its block so the chosen path stays visually dominant. */
  dimmed?: boolean;
  edgeStyle: StepEdgeStyle | undefined;
  geom: RowGeometry;
  laneIndex: (id: string) => number;
  rowTopPad: (y: number) => number;
  rowBottomPad: (y: number) => number;
}) {
  const fromI = laneIndex(row.fromLaneId);
  const toI = laneIndex(row.toLaneId);
  if (toI < 0) return null;
  const blockX = toI * LANE_WIDTH + BLOCK_INSET;
  const blockW = LANE_WIDTH - BLOCK_INSET * 2;
  const blockCx = blockX + blockW / 2;
  const { blockCy: cy } = blockYBounds(row.y, toI, geom, rowTopPad(row.y), rowBottomPad(row.y));
  const widgetColor = !active && edgeStyle?.color ? edgeStyle.color : undefined;
  // Saga compensation (unwind) — amber + dashed, matching the flow styling, so
  // the rollback direction stands apart from forward calls.
  const compensation = edgeStyle?.compensation === true;
  // `dist` — choreographed span, no coordinator (PROCESS.md §2).
  const dist = edgeStyle?.dist === true;
  const stroke = active ? "#38bdf8" : compensation ? "#f59e0b" : widgetColor ?? "#94a3b8";
  const marker = active ? "sw-arrow-active" : "sw-arrow";
  const dashed = compensation || !!edgeStyle?.dashed;
  const strokeW = active ? 2 : edgeStyle?.width ?? 1.5;
  const sameLane = fromI === toI;
  const callerX = fromI >= 0 ? fromI * LANE_WIDTH + LANE_WIDTH / 2 : blockCx;
  let arrowD: string;
  if (sameLane) {
    arrowD = `M ${blockX + blockW} ${cy - 6} q 14 0 14 12 q 0 12 -14 12`;
  } else {
    const endX = callerX < blockCx ? blockX : blockX + blockW;
    arrowD = `M ${callerX} ${cy} L ${endX} ${cy}`;
  }
  return (
    <g data-dist={dist ? "true" : undefined} opacity={dimmed && !active ? 0.45 : 1}>
      {dist && (
        <text x={blockX} y={cy - 12} fontSize={8} fontWeight={600} fill="#a78bfa">dist</text>
      )}
      <path
        d={arrowD}
        data-compensation={compensation ? "true" : undefined}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW}
        strokeDasharray={dashed ? "6 5" : undefined}
        opacity={edgeStyle?.opacity ?? 1}
        markerEnd={svgFragmentUrl(marker)}
      />
      {isEntry && !sameLane && (
        <circle cx={callerX} cy={cy} r={5} fill={stroke} />
      )}
      {/* The trailing call comment is drawn by the dedicated over-all-rows pass
          in the parent (shared by both variants); this arrow only draws the edge. */}
    </g>
  );
}
